import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../context/AuthContext";
import RowEquipementsCisco from "./RowEquipementsCisco";
import Form from "../../../components/Forms/Form";
import Loader from "../../../components/Loader";
import { useLocation, Link } from "react-router-dom";
import { EquipementSchema } from "../../../components/Forms/schema/YupSchema";
import CarousselNav from "../../../components/Card/CarousselNav";
import Table from "../../../components/Table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Equipementscolumns } from "../../../components/headerContent";
import NumberCard from "../../../components/Card/NumberCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faEye, faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { convertDateAndReturn } from "../../../scripts/utils";
import { useTranslation } from "react-i18next";

export default function EquipementCisco({ visio }) {
  const { apiUrl, user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  const [isBU, setIsBU] = useState(false);
  const [resStatus, setResStatus] = useState(null);
  const [equipementsByType, setEquipementsByType] = useState([]);
  const [equipementExport, setEquipementExcel] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [valuesType, setValuesType] = useState([]);
  const [filteredColumns, setFilteredColumns] = useState([]);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const equipementId = searchParams.get("equipementId");
  const [filteredData, setFilteredData] = useState([]);
  const isSuperAdmin = user?.roles?.includes("ROLE_SUPERADMIN");

  const isEquipmentActive = (equipment) => String(equipment?.is_active) === "1";

  const getVisibleEquipements = (equipementsData = []) => {
    const visibleEquipements = isSuperAdmin
      ? equipementsData
      : equipementsData.filter(isEquipmentActive);

    return [...visibleEquipements].sort(
      (a, b) => Number(isEquipmentActive(b)) - Number(isEquipmentActive(a))
    );
  };

  const getAllEquipements = async () => {
    const res = await axios.get(`${apiUrl}equipement/index`, {
      withCredentials: true,
    });
    setIsBU(res.data.some((equipment) => equipment.bu !== null));

    return res.data.filter((item) => {
      if (visio) {
        return item.famille && item.famille.nom === "Visio"
      } else {
        return item.famille ? item.famille.nom !== "Visio" && item.famille.nom !== "FIREWALL" : item
      }
    }).map((item) => ({
      ...item,
      is_covered: item.is_covered === "YES" ? "OUI" : item.is_covered === "NO" ? "NON" : "",
    }));
  };

  const equipements = useQuery({
    queryKey: ["equipements"],
    queryFn: getAllEquipements
  });

  // Request to get all sites
  const getAllSites = async (entreprise) => {
    if (user.entrepriseId) {
      const res = await axios(`${apiUrl}equipement/sites/${entreprise}`, {
        method: "GET",
        withCredentials: true,
      });
      return res.data;
    }
  };

  const sites = useQuery({
    queryKey: ["sites", user.entrepriseId],
    queryFn: () => getAllSites(user.entrepriseId)
  });

  useEffect(() => {
    queryClient.invalidateQueries(["sites", user.entrepriseId]);
  }, [user.entrepriseId]);

  useEffect(() => {
    equipements.refetch();
  }, [visio, user.entrepriseId]);

  useEffect(() => {
    filterEquipmentByType();
  }, [currentSlide, equipements.data]);

  useEffect(() => {
    if (equipementsByType && isSuperAdmin) {
      const filteredEquipements = equipementsByType.map(({
        sn_valid,
        is_active,
        entreprise,
        holding,
        famille,
        site,
        id,
        software_version,
        ...rest
      }) => {
        const endOfSupportDate = new Date(famille?.end_of_support);
        const year = endOfSupportDate.getFullYear();
        const end_of_support = year === 1970 ? "Non annoncé" : convertDateAndReturn(famille?.end_of_support);

        return {
          ...rest,
          end_of_support,
          product_link: famille?.product_link || "",
          product: famille?.product || "",
          site: site?.nom || ""
        };
      });
      setEquipementExcel([filteredEquipements]);
    }
  }, [equipementsByType, isSuperAdmin])

  useEffect(() => {
    // Filtrer les colonnes en fonction de la valeur de isBU
    setFilteredColumns(
      Equipementscolumns.filter((column) => {
        // Exclure la colonne "BU" si isBU est false
        if (column.accessor === "bu" && !isBU) {
          return false;
        }
        return true;
      })
    );
  }, [isBU, equipements.data]);

  useEffect(() => {
    if (!equipements.isFetching || equipements.data && equipements.data.length > 0) {
      const visibleEquipements = getVisibleEquipements(equipements.data);

      if (equipementId) {
        const filteredData = visibleEquipements.filter(cont => cont.serial_number === equipementId);
        setFilteredData(filteredData.length > 0 ? filteredData : visibleEquipements);
      } else {
        setFilteredData(visibleEquipements);
      }
    }
  }, [equipements.data, equipementId, visio, isSuperAdmin]);

  const filterEquipmentByType = () => {
    if (!equipements.data) return;
    const visibleEquipements = getVisibleEquipements(equipements.data);

    setValuesType(["Tous", ...new Set(
      visibleEquipements
        .map((equipement) => equipement.famille?.nom || "Autres")
        .filter(Boolean)
    )]);

    const familleNoms = ["Tous", ...new Set(
      visibleEquipements.map((equipement) => equipement.famille?.nom || "Autres")
    )];

    const currentFamilleNom = familleNoms[currentSlide] || "Tous";

    const equipementsFiltered = visibleEquipements.filter((equipement) => {
      const familleNom = equipement.famille?.nom || "Autres";
      return currentFamilleNom === "Tous" || familleNom === currentFamilleNom;
    });

    setEquipementsByType(equipementsFiltered);
  };

  const formContentNew = [
    { id: "serial_number", type: "text", title: "Numéro de série" },
    { id: "ip", type: "text", title: "IP" },
    { id: "device_name", type: "text", title: "Nom de l'appareil" },
    { id: "software_version", type: "text", title: "Version du logiciel" },
    { id: "BU", type: "text", title: "BU" },
    {
      id: "site",
      type: "select",
      values: !sites.isLoading && Array.isArray(sites.data)
        ? sites.data.map((site) => {
          return { value: site.id, label: site.nom };
        })
        : [],
      title: "Site",
    },
  ];

  const createNewEquipement = async (data) => {
    const updatedData = {
      ...data,
      holding: user.holdingId,
      entreprise: user.entrepriseId,
      is_active: 1,
      sn_valid: 1,
      source: "ECP"
    };


    const res = await axios(`${apiUrl}equipement/new`, {
      method: "post",
      withCredentials: true,
      data: updatedData,
    });

    return res.data;
  };

  // // Show loader until all data is fetched
  if (equipements.isFetching || equipements.isLoading && !filteredData.length || sites.isLoading ) {
    return <Loader />;
  }

  if (!equipements.isLoading && equipements.data && equipements.data.length === 0 || !equipements.isLoading && !equipements.data) {
    return (
      <div className="ecp-container">
        <div className="ecp-container-content-error">
          <div className="error-container">
            <img src="../assets/ecp_error.svg" alt="icône d'erreur" />
            <div className="error-container-text">
              <h1 className="page-title">
                {t("Aucun équipement disponible")}
              </h1>
              <p className="page-link_home">
                <Link to="/"> {t("Revenir à l'accueil")}</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ecp-container">
      <div className="ecp-container-content">
        <div className="ecp-container-content-header">
          <div className="ecp-container-content-header-title">
            <div className="page-title page-logo">
              {!visio && (
                <img src="../assets/cisco_logo.png" alt="Logo de Cisco Webex" />
              )}
              <h1 className="page-title page-logo">
                {t("Equipements")} {visio ? "Visio" : "Cisco"}
              </h1>
            </div>
            {!visio && <CarousselNav
              arrayValues={valuesType}
              currentSlide={currentSlide}
              setCurrentSlide={setCurrentSlide}
            />}
          </div>
          <div className="content-buttons-equipements">
            <div className="content-buttons-equipements-container container-file-saver-csv">
              {equipementId && (
                <div className="page-title page-link-equipement">
                  <Link to={"/smartnet"} className="button button-new"><FontAwesomeIcon icon={faChevronLeft} /> {t('Retour')}</Link>
                </div>
              )}
              {!visio && isSuperAdmin &&
                <div className={`${!isSuperAdmin ? 'no-admin-file-saver' : ''} container-file-saver-csv`}>
                  <Form
                    resStatus={resStatus}
                    setResStatus={setResStatus}
                    formContent={formContentNew}
                    schema={EquipementSchema}
                    submitFunction={createNewEquipement}
                    title="Ajouter un équipement"
                    action="new"
                  />
                </div>
              }
            </div>
          </div>
        </div>
        <section className="page-section">
          <NumberCard
            title={`${t("Nombre d'équipements disponibles")} ${currentSlide !== 0 ? valuesType[currentSlide] : visio ? "visio" : ""
              }`}
            number={
              currentSlide !== 0
                ? equipementsByType.length
                : filteredData.length
            }
            logo={visio ? faEye : faCircleCheck}
            color={visio ? "#997ADB" : "#AEC353"}
            stylesIcons={visio ? "logo-visio" : "logo-certificats"}
          />
        </section>
        <div className='cato-content-table'>
          <Table
            tableData={
              currentSlide !== 0 ? equipementsByType : filteredData
            }
            rowComponent={RowEquipementsCisco}
            tableColumns={filteredColumns}
            FileSaverExcelData={equipementExport}
            FileSaverExcelName="Equipements"
            getRowClassName={(item) => (
              isSuperAdmin && !isEquipmentActive(item)
                ? "statistics-table-row-inactive"
                : ""
            )}
          />
        </div>
      </div>
    </div>
  );
}
